import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BankOutlined,
  BellOutlined,
  CameraOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  GlobalOutlined,
  KeyOutlined,
  LaptopOutlined,
  LoadingOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Skeleton, Switch } from 'antd';

import { StatCard, Tag } from '../components/ds';
import { api } from '../lib/api';
import { authApi } from '../lib/authApi';
import { learnApi } from '../lib/api/learn';
import { educationApi } from '../lib/wikiApi';
import { getPushState, enablePush, disablePush, type PushState } from '../lib/push';
import './ProfilePage.css';

/**
 * /profile на wiki.brasol.ru — визуально 1-в-1 с CRM-профилем (тот же кит:
 * hero с лого-рыбкой и кольцом готовности, KPI-полоса, личные данные /
 * безопасность / push, завершённость / доступ / лента активности).
 *
 * Данные — те же endpoints что у CRM (shared cookie .brasol.ru): /auth/me,
 * /auth/me/stats, /users/me/avatar. Правка здесь видна и в CRM (один User).
 * Для iiko-сотрудника — лёгкая заглушка (у них нет редактируемого профиля).
 */

interface CrmUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  phone: string | null;
  avatarPath: string | null;
  role: { code: string; name: string };
  organization?: { name: string };
  isActive: boolean;
}
interface ProfileActivityItem {
  type: 'invoice' | 'partner' | 'contract';
  title: string;
  subtitle?: string | null;
  amount?: number | null;
  at: string;
}
interface ProfileStats {
  points: number;
  partners: number;
  cities: number;
  invoicesThisMonth: number;
  cityNames: string[];
  activity: ProfileActivityItem[];
}

function fmtDateTimeRu(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function detectDevice(): string {
  if (typeof navigator === 'undefined') return '—';
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Macintosh|Mac OS/i.test(ua)) return 'Mac';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad/i.test(ua)) return 'iOS';
  return 'Компьютер';
}
function initialsOf(first: string | null, last: string | null, email: string): string {
  const p: string[] = [];
  if (last) p.push(last.trim().charAt(0));
  if (first) p.push(first.trim().charAt(0));
  if (p.length === 0 && email) p.push(email.charAt(0).toUpperCase());
  return p.join('').toUpperCase().slice(0, 2) || '?';
}

/**
 * HUB 2.0 (W5): обучение в профиле — очки и серия, сертификаты (W3
 * /wiki/education/certificates/mine), навыки, переключатель «Участвовать в
 * рейтинге команды». Для CRM-юзера и сотрудника iiko одинаково (ownerKey).
 */
function LearningProfile(): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const gam = useQuery({ queryKey: ['wiki', 'gamification', 'mine'], queryFn: () => educationApi.gamificationMine(), retry: false });
  const certs = useQuery({ queryKey: ['wiki', 'education', 'certificates', 'mine'], queryFn: () => learnApi.myCertificates(), retry: false });
  const skills = useQuery({ queryKey: ['wiki', 'education', 'skills', 'mine'], queryFn: () => educationApi.mySkills(), retry: false });
  const courses = useQuery({ queryKey: ['wiki', 'education', 'courses'], queryFn: () => educationApi.listCourses(), retry: false });
  const optIn = useMutation({
    mutationFn: (v: boolean) => educationApi.gamificationPrefs(v),
    onSuccess: (r) => {
      void message.success(r.leaderboardOptIn ? 'Вы участвуете в рейтинге команды' : 'Вы скрыты из рейтинга команды');
      void qc.invalidateQueries({ queryKey: ['wiki', 'gamification'] });
    },
    onError: (e: Error) => void message.error(e.message),
  });
  const done = (courses.data ?? []).filter((c) => c.isCompleted).length;
  const inProgress = (courses.data ?? []).filter((c) => !c.isCompleted && c.progressPercent > 0).length;
  const certList = certs.data ?? [];
  const skillList = (skills.data ?? []).filter((s) => s.level > 0);
  const earnedBadges = (gam.data?.badges ?? []).filter((b) => b.earned).length;

  return (
    <>
      <div className="pf-kpis">
        <StatCard label="Очки обучения" value={gam.data ? gam.data.points.toLocaleString('ru-RU') : '—'} hint={gam.data ? `серия ${gam.data.streakDays} дн.` : undefined} to="/points" />
        <StatCard label="Курсов пройдено" value={done} hint={inProgress ? `в процессе: ${inProgress}` : undefined} to="/education" tone="green" />
        <StatCard label="Сертификатов" value={certList.length} hint={certList.some((c) => c.isExpired) ? 'есть просроченные' : undefined} hintColor="var(--fk-error)" tone="indigo" />
        <StatCard label="Бейджей" value={earnedBadges} hint={gam.data ? `из ${gam.data.badges.length}` : undefined} to="/points" tone="gold" />
      </div>
      <section className="pf-card">
        <header className="pf-card-head"><h3><SafetyCertificateOutlined />Сертификаты и навыки</h3></header>
        {certList.length === 0 ? (
          <p className="pf-card-sub">Сертификатов пока нет — они выдаются за курсы с сертификатом.</p>
        ) : (
          <div className="pf-certs">
            {certList.map((c) => (
              <a key={c.courseId} className="pf-cert" href={educationApi.certificatePdfUrl(c.courseId)} target="_blank" rel="noopener noreferrer">
                <SafetyCertificateOutlined className="pf-cert-ico" />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span className="pf-cert-title">{c.courseName}</span>
                  <span className="pf-cert-sub">
                    № {c.number} · выдан {new Date(c.issuedAt).toLocaleDateString('ru-RU')}
                    {c.expiresAt ? ` · до ${new Date(c.expiresAt).toLocaleDateString('ru-RU')}` : ' · бессрочно'}
                  </span>
                </span>
                {c.isExpired ? <Tag color="red">истёк</Tag> : <Tag color="green">PDF</Tag>}
              </a>
            ))}
          </div>
        )}
        {skillList.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
            {skillList.map((sk) => (
              <Tag key={sk.id} color={sk.expired ? 'red' : sk.level >= 3 ? 'green' : 'default'} title={sk.expiresAt ? `до ${new Date(sk.expiresAt).toLocaleDateString('ru-RU')}` : undefined}>
                {sk.name} · {sk.levelName}
              </Tag>
            ))}
          </div>
        )}
      </section>
      <section className="pf-card">
        <header className="pf-card-head"><h3><TeamOutlined />Рейтинг и настройки</h3></header>
        <label className="pf-setting">
          <span>
            <b>Участвовать в рейтинге команды</b>
            <span className="pf-card-sub" style={{ display: 'block', margin: 0 }}>Ваше имя и очки видны коллегам по точке. Можно выключить в любой момент.</span>
          </span>
          <Switch checked={!!gam.data?.leaderboardOptIn} loading={gam.isLoading || optIn.isPending} disabled={!gam.data} onChange={(v) => optIn.mutate(v)} />
        </label>
        <Link to="/notifications" className="pf-setting pf-setting--link"><span><BellOutlined /> Уведомления</span><span>→</span></Link>
        <Link to="/documents" className="pf-setting pf-setting--link"><span><FileTextOutlined /> Мои документы</span><span>→</span></Link>
        <Link to="/points" className="pf-setting pf-setting--link"><span><SafetyCertificateOutlined /> Мои очки и бейджи</span><span>→</span></Link>
      </section>
    </>
  );
}

export function ProfilePage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { message } = App.useApp();

  const meQuery = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const isCrm = meQuery.data?.type === 'crm_user';

  const crmMeQuery = useQuery({
    queryKey: ['crm-auth', 'me'],
    queryFn: () => api.get<{ user: CrmUser }>('/auth/me'),
    enabled: isCrm,
    staleTime: 60_000,
    retry: false,
  });
  const statsQuery = useQuery({
    queryKey: ['crm-auth', 'me', 'stats'],
    queryFn: () => api.get<ProfileStats>('/auth/me/stats'),
    enabled: isCrm,
    retry: false,
    staleTime: 60_000,
  });

  const [values, setValues] = useState({ lastName: '', firstName: '', middleName: '', phone: '' });
  const [pushState, setPushState] = useState<PushState | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const u = crmMeQuery.data?.user;
  useEffect(() => {
    if (u) setValues({ lastName: u.lastName ?? '', firstName: u.firstName ?? '', middleName: u.middleName ?? '', phone: u.phone ?? '' });
  }, [u]);
  useEffect(() => { void (async () => { try { setPushState(await getPushState()); } catch { /* no-op */ } })(); }, []);

  const pushOn = pushState === 'subscribed';
  const deviceName = useMemo(() => detectDevice(), []);

  const checklist = useMemo(() => [
    { key: 'name', label: 'ФИО заполнено', done: Boolean(values.lastName.trim() && values.firstName.trim()) },
    { key: 'middle', label: 'Отчество указано', done: Boolean(values.middleName.trim()) },
    { key: 'phone', label: 'Телефон подтверждён', done: Boolean(values.phone.trim()) },
    { key: 'photo', label: 'Загружено фото', done: Boolean(u?.avatarPath) },
    { key: 'push', label: 'Включены push-уведомления', done: pushOn },
  ], [values, u?.avatarPath, pushOn]);
  const completion = Math.round((checklist.filter((c) => c.done).length / checklist.length) * 100);

  const saveMutation = useMutation({
    mutationFn: () => api.patch('/auth/me', {
      firstName: values.firstName.trim(), lastName: values.lastName.trim(),
      middleName: values.middleName.trim(), phone: values.phone.trim(),
    }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['wiki-auth', 'me'] }),
        qc.invalidateQueries({ queryKey: ['crm-auth', 'me'] }),
      ]);
      void message.success('Профиль сохранён — изменения видны и в CRM');
    },
    onError: (err: Error) => void message.error(err.message || 'Не удалось сохранить'),
  });

  const onAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) { void message.error('Только JPG / PNG / WebP'); return; }
    if (f.size > 5 * 1024 * 1024) { void message.error('Файл больше 5 МБ'); return; }
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch('/api/users/me/avatar', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error((await res.text()) || 'Не удалось загрузить');
      setAvatarVersion((v) => v + 1);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['wiki-auth', 'me'] }),
        qc.invalidateQueries({ queryKey: ['crm-auth', 'me'] }),
      ]);
      void message.success('Фото обновлено — оно же в CRM');
    } catch (err) { void message.error((err as Error).message || 'Не удалось загрузить'); }
  };

  const togglePush = async (enable: boolean): Promise<void> => {
    setPushBusy(true);
    try {
      const st = enable ? await enablePush() : await disablePush();
      setPushState(st);
    } catch (e) { void message.error((e as Error).message); } finally { setPushBusy(false); }
  };

  if (meQuery.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (meQuery.error) return <Alert type="error" showIcon message="Не удалось загрузить профиль" description={(meQuery.error as Error).message} />;

  // ── iiko-сотрудник: профиль из iiko (не редактируется) + обучение ──
  if (!isCrm) {
    const me = meQuery.data!;
    return (
      <div className="pf-page">
        <section className="pf-card" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div className="pf-avatar" style={{ width: 72, height: 72, fontSize: 26, border: 'none' }}>{(me.fullName || 'С').slice(0, 1).toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 className="pf-name" style={{ margin: 0 }}>{me.fullName}</h1>
            <div className="pf-card-sub" style={{ margin: '4px 0 0' }}>{me.roleName || 'Сотрудник'} · {me.tradingPointName || 'без точки'}</div>
          </div>
          <button type="button" className="pf-btn ghost" onClick={() => navigate('/change-password')}>
            <KeyOutlined /> Сменить пароль
          </button>
        </section>
        <Alert type="info" showIcon message="Данные из iiko" description="Имя, телефон и точка обновляются автоматически из iiko. Если они неверны — скажите управляющему." />
        <LearningProfile />
      </div>
    );
  }

  if (crmMeQuery.isLoading || !u) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (crmMeQuery.error) return <Alert type="error" showIcon message="Не удалось загрузить профиль CRM" description={(crmMeQuery.error as Error).message} />;

  const stats = statsQuery.data ?? null;
  const fullName = [u.lastName, u.firstName, u.middleName].filter(Boolean).join(' ') || u.email;
  const initials = initialsOf(u.firstName, u.lastName, u.email);
  const roleName = u.role.name ?? 'Пользователь';
  const orgName = u.organization?.name ?? 'БРАСОЛ';
  const isAdminish = /admin|director|owner|администр|директор/i.test(`${u.role.code} ${u.role.name}`);
  const accessLabel = isAdminish ? 'Полный доступ к разделам сети' : `Доступ: ${stats?.points ?? 0} ТТ`;
  const avatarUrl = u.avatarPath ? `/api/users/avatar/${u.id}?v=${avatarVersion}` : null;

  const feedIcon = (t: ProfileActivityItem['type']) =>
    t === 'invoice' ? <FileTextOutlined /> : t === 'partner' ? <TeamOutlined /> : <SafetyCertificateOutlined />;
  const feedTone: Record<string, { bg: string; fg: string }> = {
    invoice: { bg: 'rgba(255,105,0,.12)', fg: '#FF6900' },
    partner: { bg: 'rgba(0,150,255,.12)', fg: '#0096FF' },
    contract: { bg: 'rgba(124,179,66,.12)', fg: '#5a8a2e' },
  };

  return (
    <div className="pf-page">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onAvatarPick} />

      {/* ─── Hero ─── */}
      <section className="pf-hero">
        <div className="pf-cover">
          <img className="pf-cover-logo-img" src="/profile-assets/fishka-lockup-white.png" alt="fishka — суши • роллы" />
          <div className="pf-cover-scatter">
            <img src="/profile-assets/s5.png" alt="" style={{ left: '34%', top: '-18px', width: 92, transform: 'rotate(-18deg)' }} />
            <img src="/profile-assets/s3.png" alt="" style={{ left: '46%', top: '58px', width: 84, transform: 'rotate(-4deg)' }} />
            <img src="/profile-assets/s7.png" alt="" style={{ left: '56%', top: '-14px', width: 90, transform: 'rotate(12deg)' }} />
            <img src="/profile-assets/s8.png" alt="" style={{ left: '66%', top: '54px', width: 74, transform: 'rotate(8deg)' }} />
          </div>
          <div className="pf-completion">
            <Ring pct={completion} />
            <div className="pf-completion-text">
              <span className="pf-completion-label">Профиль заполнен</span>
              <span className="pf-completion-hint">{completion === 100 ? 'Всё готово' : `Осталось: ${checklist.filter((c) => !c.done).map((c) => c.label.toLowerCase()).slice(0, 2).join(', ')}`}</span>
            </div>
          </div>
        </div>
        <div className="pf-hero-body">
          <div className="pf-avatar-block">
            <div className="pf-avatar">
              {avatarUrl ? <img src={avatarUrl} alt="" /> : initials}
              <button className="pf-avatar-cam" title="Загрузить фото" onClick={() => fileRef.current?.click()}><CameraOutlined /></button>
            </div>
          </div>
          <div className="pf-identity">
            <div className="pf-name-row">
              <h1 className="pf-name">{fullName}</h1>
              <span className="pf-verified" title="Аккаунт подтверждён"><SafetyCertificateOutlined /></span>
            </div>
            <div className="pf-role-row">
              <span className="pf-role-chip"><SafetyOutlined />{roleName}</span>
              <span className="pf-org"><BankOutlined />{orgName}</span>
              {u.isActive && <span className="pf-status"><span className="pf-status-dot" />Активен</span>}
            </div>
            <div className="pf-contact-row">
              <span><MailOutlined />{u.email}</span>
              {u.phone && <span><PhoneOutlined />{u.phone}</span>}
            </div>
          </div>
          <div className="pf-hero-actions">
            <Link to="/change-password" className="pf-btn ghost"><KeyOutlined />Сменить пароль</Link>
            <button className="pf-btn primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? <LoadingOutlined spin /> : <CheckOutlined />}Сохранить
            </button>
          </div>
        </div>
      </section>

      {/* ─── KPI ─── */}
      <div className="pf-kpis">
        {[
          { icon: <ShopOutlined />, value: stats?.points ?? '—', label: 'Точек в зоне' },
          { icon: <TeamOutlined />, value: stats?.partners ?? '—', label: 'Партнёров' },
          { icon: <GlobalOutlined />, value: stats?.cities ?? '—', label: 'Городов' },
          { icon: <FileTextOutlined />, value: stats?.invoicesThisMonth ?? '—', label: 'Счетов за месяц' },
        ].map((k, i) => (
          <div className="pf-kpi" key={i}>
            <span className="pf-kpi-ico">{k.icon}</span>
            <div><div className="pf-kpi-value">{k.value}</div><div className="pf-kpi-label">{k.label}</div></div>
          </div>
        ))}
      </div>

      {/* ─── Обучение (HUB 2.0) ─── */}
      <LearningProfile />

      {/* ─── Columns ─── */}
      <div className="pf-cols">
        <div className="pf-col-main">
          <section className="pf-card">
            <header className="pf-card-head"><h3><UserOutlined />Личные данные</h3></header>
            <div className="pf-grid2">
              <Field label="Email" value={u.email} disabled />
              <Field label="Телефон" value={values.phone} onChange={(v) => setValues((s) => ({ ...s, phone: v }))} />
              <Field label="Имя" value={values.firstName} onChange={(v) => setValues((s) => ({ ...s, firstName: v }))} />
              <Field label="Фамилия" value={values.lastName} onChange={(v) => setValues((s) => ({ ...s, lastName: v }))} />
              <Field label="Отчество" value={values.middleName} onChange={(v) => setValues((s) => ({ ...s, middleName: v }))} placeholder="Укажите отчество" />
            </div>
          </section>

          <section className="pf-card">
            <header className="pf-card-head"><h3><SafetyOutlined />Безопасность</h3></header>
            <div className="pf-sec-grid">
              <SecCell icon={<KeyOutlined />} title="Пароль" meta="Управление паролем" tone="ok" onClick={() => navigate('/change-password')} />
              <SecCell icon={<LaptopOutlined />} title="Это устройство" meta={deviceName} tone="ok" />
            </div>
          </section>

          <section className="pf-card">
            <header className="pf-card-head">
              <h3><BellOutlined />Push-уведомления</h3>
              <button className={`pf-switch${pushOn ? ' is-on' : ''}`} disabled={pushBusy} onClick={() => void togglePush(!pushOn)}><span /></button>
            </header>
            <p className="pf-card-sub">
              Получайте важные события в реальном времени на этом устройстве
              {pushState === 'denied' ? ' — браузер запретил уведомления, разрешите их в настройках сайта' : ''}.
              Что именно присылать — в <Link to="/notifications">настройках уведомлений</Link>.
            </p>
          </section>
        </div>

        <div className="pf-col-side">
          <section className="pf-card">
            <header className="pf-card-head"><h3><CheckOutlined />Завершённость профиля</h3></header>
            <div className="pf-prog-row">
              <div className="pf-prog-track"><div className="pf-prog-fill" style={{ width: `${completion}%` }} /></div>
              <span className="pf-prog-pct">{completion}%</span>
            </div>
            <ul className="pf-checklist">
              {checklist.map((c) => (
                <li key={c.key} className={c.done ? 'is-done' : ''}>
                  <span className="pf-check-ic">{c.done ? <CheckOutlined style={{ fontSize: 13 }} /> : <PlusOutlined style={{ fontSize: 13 }} />}</span>
                  {c.label}
                </li>
              ))}
            </ul>
          </section>

          <section className="pf-card">
            <header className="pf-card-head"><h3><EnvironmentOutlined />Доступ и зоны ответственности</h3></header>
            <div className="pf-access-row">
              <div className="pf-access-stat"><b>{stats?.points ?? '—'}</b><span>торговых точек</span></div>
              <div className="pf-access-stat"><b>{stats?.cities ?? '—'}</b><span>городов</span></div>
              <div className="pf-access-stat"><b>{stats?.partners ?? '—'}</b><span>партнёров</span></div>
            </div>
            {stats && stats.cityNames.length > 0 && (
              <div className="pf-city-chips">{stats.cityNames.map((c) => <span className="pf-city-chip" key={c}>{c}</span>)}</div>
            )}
            <div className="pf-access-perm"><SafetyCertificateOutlined />{accessLabel}</div>
          </section>

          {stats && stats.activity.length > 0 && (
            <section className="pf-card">
              <header className="pf-card-head"><h3><ClockCircleOutlined />Лента активности</h3></header>
              <ul className="pf-feed">
                {stats.activity.map((a, i) => (
                  <li key={i}>
                    <span className="pf-feed-ico" style={{ background: feedTone[a.type]?.bg, color: feedTone[a.type]?.fg }}>{feedIcon(a.type)}</span>
                    <div className="pf-feed-body">
                      <div className="pf-feed-text">{a.title}{a.subtitle ? ` · ${a.subtitle}` : ''}</div>
                      <div className="pf-feed-meta">
                        {a.amount != null && <span className="pf-feed-amount">{a.amount.toLocaleString('ru-RU')} ₽</span>}
                        <span className="pf-feed-time">{fmtDateTimeRu(a.at)}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Вспомогательные ───

function Ring({ pct, size = 64, stroke = 6 }: { pct: number; size?: number; stroke?: number }): JSX.Element {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="pf-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.25)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#fff" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fill="#fff" fontSize="15" fontWeight="800">{pct}%</text>
    </svg>
  );
}

function Field({ label, value, onChange, disabled, placeholder }: { label: string; value: string; onChange?: (v: string) => void; disabled?: boolean; placeholder?: string }): JSX.Element {
  return (
    <label className="pf-field">
      <span className="pf-field-label">{label}</span>
      <input className={`pf-input${disabled ? ' is-disabled' : ''}`} value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange?.(e.target.value)} />
    </label>
  );
}

function SecCell({ icon, title, meta, tone, cta, onClick }: { icon: JSX.Element; title: string; meta: string; tone: 'ok' | 'warn'; cta?: string; onClick?: () => void }): JSX.Element {
  return (
    <div className="pf-sec-cell" onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
      <span className="pf-sec-ico">{icon}</span>
      <div className="pf-sec-body">
        <div className="pf-sec-title">{title}</div>
        <div className={`pf-sec-meta tone-${tone}`}>{meta}</div>
      </div>
      {cta && <button className="pf-sec-cta" type="button">{cta}</button>}
    </div>
  );
}
